import type { MaybeRef } from "vue";
import { computed, inject, provide, ref, toValue } from "vue";

const SortableKey = Symbol("Sortable");

interface SortState {
  field: string
  direction: "DESC" | "ASC"
}

export function createSortable(sortableFields: MaybeRef<Partial<Record<string, string>>>, initialSort?: SortState) {
  const sort = ref<string | null>(initialSort?.field || null);
  const sortDirection = ref<"DESC" | "ASC">(initialSort?.direction || "ASC");
  // Page/list baseline (initialSort, setDefaultSort, or setSort with
  // updateDefault). VSort Reset restores this - not the user's last preference.
  const defaultSortField = ref<string | null>(initialSort?.field || null);
  const defaultSortDirection = ref<"DESC" | "ASC">(initialSort?.direction || "ASC");
  const sortableFieldsArray = computed(() => Object.keys(toValue(sortableFields)));

  // When a sort is applied programmatically (e.g. a list's default sort on
  // navigation) the surrounding view is usually already reloading for another
  // reason. This flag lets a sort consumer (e.g. VSort) skip its own reload for
  // that single change so we don't fire a duplicate request.
  const suppressNextReload = ref(false);

  const isAtDefaultSort = computed(() =>
    sort.value === defaultSortField.value
    && (sort.value === null || sortDirection.value === defaultSortDirection.value),
  );

  function setDefaultSort(defaultSort?: SortState) {
    defaultSortField.value = defaultSort?.field || null;
    defaultSortDirection.value = defaultSort?.direction || "ASC";
  }

  function setSort(
    newSort: SortState | undefined,
    options: { silent?: boolean, updateDefault?: boolean } = {},
  ) {
    const nextField = newSort?.field || null;
    const nextDirection = newSort?.direction || "ASC";
    const willChange = sort.value !== nextField || sortDirection.value !== nextDirection;
    const updateDefault = options.updateDefault ?? true;

    if (options.silent && willChange) {
      suppressNextReload.value = true;
    }

    sort.value = nextField;
    sortDirection.value = nextDirection;

    if (updateDefault) {
      defaultSortField.value = nextField;
      defaultSortDirection.value = nextDirection;
    }
  }

  function resetToDefaultSort() {
    if (isAtDefaultSort.value) {
      return;
    }

    sort.value = defaultSortField.value;
    sortDirection.value = defaultSortDirection.value;
  }

  function toggleSortDirection() {
    sortDirection.value = sortDirection.value === "DESC" ? "ASC" : "DESC";
  }

  function sortingEnabled() {
    return sort.value;
  }

  function setSortUrlParams(urlParams: URLSearchParams) {
    if (sortingEnabled()) {
      urlParams.append(`order[${sort.value}]`, sortDirection.value);
    }
  }

  function isSortable(field: string) {
    return sortableFieldsArray.value.includes(field);
  }

  return {
    toggleSortDirection,
    sortingEnabled,
    setSortUrlParams,
    sort,
    sortDirection,
    sortableFields,
    isSortable,
    setSort,
    setDefaultSort,
    resetToDefaultSort,
    isAtDefaultSort,
    suppressNextReload,
  };
}

type CreateSortableParameters = Parameters<typeof createSortable>;
export function provideSortable(...params: CreateSortableParameters) {
  const instance = createSortable(...params);
  provide(SortableKey, instance);
  return instance;
}

type UseSortable = ReturnType<typeof createSortable>;
export function useSortable() {
  const instance = inject<UseSortable>(SortableKey);

  if (!instance) {
    throw new Error("Run provideSortable before useSortable.");
  }

  return instance;
}

export function useOptionalSortable(): UseSortable | null {
  return inject<UseSortable | null>(SortableKey, null);
}
